import ButtonDropdown from '@rio-cloud/rio-uikit/ButtonDropdown';
import type { MenuItemProps as MenuItem } from '@rio-cloud/rio-uikit/components/menuItems/MenuItem';
import SortArrows from '@rio-cloud/rio-uikit/SortArrows';
import classNames from 'classnames';
import { type ReactNode, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ProductTypedFormattedMessage } from '../../common/product/ProductTypedFormattedMessage';
import { ProductType } from '../../common/product/product';
import { isTermedLevelSelectable } from '../../common/termedLevel/termedLevelSelectionValidationService';
import { PeriodUnit, type TermedLevel } from '../../common/termedLevel/types';
import { DATE_FORMAT } from '../../common/utils/dateUtil';
import { formatDurationUnit } from '../../common/utils/formatDurationUnit';
import { getRenewsToProductLabelFromContract } from '../../common/utils/productLabelUtil';
import { ContractResourceName } from '../contracts/ContractResourceName';
import { ContractState } from '../contracts/ContractState';
import { useSorting } from '../contracts/useSorting';

interface Props {
    contracts: TermedLevel[];
    productType: ProductType;
    canConfigureRenewal: boolean;
    onConfigureRenewal: (contract: TermedLevel) => void;
}

interface Column {
    id: string;
    propertyName: keyof TermedLevel;
    label: ReactNode;
    width?: string;
}

const tableClassName = classNames(
    'table',
    'table-layout-fixed',
    'table-column-overflow-hidden',
    'table-condensed',
    'table-sticky',
    'table-sticky-in-container',
    'table-hover',
    'table-head-filled',
    'margin-bottom-0'
);

const sizeVariantByLevel: Record<string, string> = {
    '10': 'S',
    '11': 'S',
    '12': 'S',
    '13': 'S',
    '14': 'S',
    '15': 'S',
    '16': 'S',
    '20': 'M',
    '21': 'M',
    '22': 'M',
    '23': 'M',
    '24': 'M',
    '25': 'M',
    '26': 'M',
    '30': 'L',
    '31': 'L',
    '32': 'L',
    '33': 'L',
    '34': 'L',
    '35': 'L',
    '36': 'L',
};

function getColumns(productType: ProductType): Column[] {
    return [
        {
            id: 'resourceName',
            propertyName: 'resourceName',
            label: (
                <ProductTypedFormattedMessage
                    productType={productType}
                    userBasedId='intl-msg:marketplace.contracts.user.table.header'
                    assetBasedId='intl-msg:marketplace.contracts.asset.table.header'
                />
            ),
            width: '17%',
        },
        {
            id: 'variantName',
            propertyName: 'variantName',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.variant' />,
            width: '10%',
        },
        {
            id: 'periodLength',
            propertyName: 'periodLength',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.period' />,
            width: '12%',
        },
        {
            id: 'startsAt',
            propertyName: 'startsAt',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.startDate' />,
            width: '13%',
        },
        {
            id: 'endsAt',
            propertyName: 'endsAt',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.endDate' />,
            width: '13%',
        },
        {
            id: 'remainingDays',
            propertyName: 'remainingDays',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.daysLeft' />,
            width: '11%',
        },
        {
            id: 'contractState',
            propertyName: 'contractState',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.status' />,
            width: '10%',
        },
        {
            id: 'renewsToLevelName',
            propertyName: 'renewsToLevelName',
            label: <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.renewsTo' />,
            width: '14%',
        },
    ];
}

function ContractPeriod({ contract }: { contract: TermedLevel }) {
    const intl = useIntl();
    return (
        <span className='label label-condensed label-default'>
            {contract.periodLength} {formatDurationUnit(contract.periodUnit, intl, contract.periodLength)}
        </span>
    );
}

function ContractDate({ value }: { value?: string }) {
    const intl = useIntl();
    return value ? intl.formatDate(value, DATE_FORMAT) : <span>–</span>;
}

function getCompactVariantName(contract: TermedLevel): string {
    const variantName = contract.variantName.replace(/\s+variant$/i, '').trim();
    const sizeVariant = variantName.match(/(?:^|\s)([sml])(?:\s+variant)?$/i);

    if (sizeVariant) {
        return sizeVariant[1].toUpperCase();
    }

    const productName = contract.productName.trim();
    if (variantName.toLocaleLowerCase() === productName.toLocaleLowerCase()) {
        const baseLevel = contract.level.replace(/-(?:ft|io)$/i, '');
        return sizeVariantByLevel[baseLevel] ?? variantName;
    }

    if (variantName.toLocaleLowerCase().startsWith(`${productName.toLocaleLowerCase()} `)) {
        return variantName.slice(productName.length).trim();
    }

    return variantName;
}

function ContractVariant({ contract }: { contract: TermedLevel }) {
    return <span className='label label-condensed label-primary label-filled'>{getCompactVariantName(contract)}</span>;
}

function RemainingDays({ contract }: { contract: TermedLevel }) {
    if (contract.remainingDays === undefined) {
        return <span>–</span>;
    }
    if (contract.remainingDays === 0) {
        return (
            <span className='text-color-warning text-medium'>
                <FormattedMessage id='intl-msg:marketplace.today' />
            </span>
        );
    }
    return (
        <span className={classNames({ 'text-color-warning text-medium': contract.criticalRemainingDays })}>
            {contract.remainingDays}{' '}
            <FormattedMessage
                id={
                    contract.remainingDays === 1
                        ? 'intl-msg:marketplace.durationUnit.day'
                        : 'intl-msg:marketplace.durationUnit.days'
                }
            />
        </span>
    );
}

function RenewalDropdown({
    contract,
    variantName,
    canConfigureRenewal,
    onConfigureRenewal,
}: {
    contract: TermedLevel;
    variantName: string;
    canConfigureRenewal: boolean;
    onConfigureRenewal: (contract: TermedLevel) => void;
}) {
    const intl = useIntl();
    const periodOptions = [
        intl.formatMessage({ id: 'intl-msg:marketplace.p1d.period.label' }),
        `12 ${formatDurationUnit(PeriodUnit.MONTH, intl, 12)}`,
        `24 ${formatDurationUnit(PeriodUnit.MONTH, intl, 24)}`,
    ];
    const noRenewalOption = intl.formatMessage({ id: 'intl-msg:marketplace.contracts.noRenew' });
    const optionLabels =
        variantName === 'S' || variantName === 'M'
            ? [noRenewalOption, ...['S', 'M'].flatMap(variant => periodOptions.map(period => `${variant} | ${period}`))]
            : [...periodOptions, noRenewalOption];
    const items: MenuItem[] = optionLabels.map(value => ({
        value,
        onSelect: () => onConfigureRenewal(contract),
    }));
    const isRenewalConfigurable = canConfigureRenewal && isTermedLevelSelectable(contract);
    const title =
        contract.renewsToLevel === undefined ? (
            <FormattedMessage id='intl-msg:marketplace.contracts.notRenewed' />
        ) : (
            getRenewsToProductLabelFromContract(contract, intl)
        );

    return (
        <div
            className={classNames({ 'cursor-not-allowed': !isRenewalConfigurable })}
            onClick={event => event.stopPropagation()}
        >
            <ButtonDropdown
                aria-label={`${intl.formatMessage({
                    id: 'intl-msg:marketplace.bookings.overview.table.renewsTo',
                })}: ${contract.resourceName ?? contract.resourceId}`}
                title={title}
                items={items}
                bsStyle='default'
                toggleButtonType='label'
                toggleClassName={classNames('label-condensed white-space-nowrap', {
                    'opacity-50': !isRenewalConfigurable,
                })}
                dropdownClassName='width-auto'
                disabled={!isRenewalConfigurable}
            />
        </div>
    );
}

function ContractTableRow({
    contract,
    productType,
    canConfigureRenewal,
    onConfigureRenewal,
}: {
    contract: TermedLevel;
    productType: ProductType;
    canConfigureRenewal: boolean;
    onConfigureRenewal: (contract: TermedLevel) => void;
}) {
    const variantName = getCompactVariantName(contract);

    return (
        <tr>
            <td>
                <span
                    className={classNames(
                        'uikit-icon',
                        productType === ProductType.USER_BASED ? 'icon-user' : 'icon-truck',
                        'margin-right-5'
                    )}
                    aria-hidden={true}
                />
                <ContractResourceName contract={contract} />
            </td>
            <td>
                <ContractVariant contract={contract} />
            </td>
            <td>
                <ContractPeriod contract={contract} />
            </td>
            <td>
                <ContractDate value={contract.startsAt} />
            </td>
            <td>
                <ContractDate value={contract.endsAt} />
            </td>
            <td>
                <RemainingDays contract={contract} />
            </td>
            <td className='text-capitalize'>
                <ContractState contractState={contract.contractState} />
            </td>
            <td>
                <RenewalDropdown
                    contract={contract}
                    variantName={variantName}
                    canConfigureRenewal={canConfigureRenewal}
                    onConfigureRenewal={onConfigureRenewal}
                />
            </td>
        </tr>
    );
}

export function BookingOverviewContractTable({
    contracts,
    productType,
    canConfigureRenewal,
    onConfigureRenewal,
}: Props) {
    const { sort, handleSortChange, sortDirectionForProperty } = useSorting<TermedLevel>('resourceName');
    const columns = useMemo(() => getColumns(productType), [productType]);

    return (
        <div className='max-height-50vh width-100pct overflow-y-auto'>
            <table className={tableClassName}>
                <colgroup>
                    {columns.map(column => (
                        <col key={column.id} style={{ width: column.width }} />
                    ))}
                </colgroup>
                <thead>
                    <tr>
                        {columns.map(column => (
                            <th
                                key={column.id}
                                className='sort-column'
                                onClick={() => handleSortChange(column.propertyName)}
                            >
                                <span>
                                    <SortArrows direction={sortDirectionForProperty(column.propertyName)} />
                                    {column.label}
                                </span>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sort(contracts).map(contract => (
                        <ContractTableRow
                            key={`${contract.productId}-${contract.resourceId}-${contract.level}-${contract.createdAt}`}
                            contract={contract}
                            productType={productType}
                            canConfigureRenewal={canConfigureRenewal}
                            onConfigureRenewal={onConfigureRenewal}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
}
